import Alert from '@mui/material/Alert';
import { PageContainer } from '../layout/page-container';
import { PageHeader } from '../layout/page-header';
import { useFeatures } from './feature-context';

import type { ReactNode } from 'react';

export type RequireFeatureProps = {
  /** A leaf feature the member must hold. Any one of a list is enough. */
  featureGuid?: string | string[];
  /** A feature group the member must hold something in. Checked when no featureGuid is given. */
  groupGuid?: string;
  children: ReactNode;
};

/**
 * The page behind a navigation row the member cannot see. The rail already hides the row, so this
 * answers the direct link: the endpoints the page would call deny it anyway, and without this the
 * member reads a page of empty widgets and failed requests instead of being told why.
 *
 * A route naming neither a feature nor a group is open — the session gate is its only requirement.
 */
export function RequireFeature({ featureGuid, groupGuid, children }: RequireFeatureProps) {
  const { hasAccess, hasGroupAccess, isLoading } = useFeatures();

  let allowed = true;
  if (featureGuid !== undefined) {
    allowed = hasAccess(featureGuid);
  } else if (groupGuid !== undefined) {
    allowed = hasGroupAccess(groupGuid);
  }

  let content = <>{children}</>;
  if (!allowed && !isLoading) {
    content = (
      <PageContainer>
        <PageHeader title="No access" description="This page is not part of what your role can reach." />
        <Alert severity="info" role="status">
          Ask an administrator at your organization to grant you access if you need this page.
        </Alert>
      </PageContainer>
    );
  }
  return content;
}
